import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
export function KidsDecodeInputs() {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState<'baseline' | 'observations'>('baseline');
  const [isGenerating, setIsGenerating] = useState(false);
  const [formData, setFormData] = useState({
    // Child Baseline
    childName: '',
    dateOfBirth: '',
    timeOfBirthOption: '',
    timeOfBirth: '',
    birthCity: '',
    birthCountry: '',
    // Optional Observations
    energyExpression: '',
    changeResponse: '',
    emotionalExpression: ''
  });
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleBaselineSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate required baseline fields
    if (!formData.childName || !formData.dateOfBirth || !formData.timeOfBirthOption || !formData.birthCity || !formData.birthCountry) {
      return;
    }
    // Validate time input if Exact or Approximate selected
    if ((formData.timeOfBirthOption === 'exact' || formData.timeOfBirthOption === 'approximate') && !formData.timeOfBirth) {
      return;
    }
    // Move to observations section
    setCurrentSection('observations');
  };
  const handleGenerateReport = () => {
    setIsGenerating(true);
    console.log('Kids Decode inputs:', formData);
    // Simulate report generation
    setTimeout(() => {
      navigate('/kids-decode-output');
    }, 3000);
  };
  const energyOptions = ['Very expressive / outward', 'Balanced', 'Quiet / inward', 'Shifts with environment'];
  const changeResponseOptions = ['Adapts quickly', 'Needs time to adjust', 'Becomes anxious or resistant', 'Becomes withdrawn'];
  const emotionalExpressionOptions = ['Express openly', 'Seek comfort', 'Withdraw silently', 'Act out physically'];
  if (isGenerating) {
    return <>
        <Helmet data-id="element-1867">
          <title data-id="element-1868">Generating Report — Kids Decode | Lazy Monk Labs</title>
          <meta name="robots" content="noindex, nofollow" data-id="element-1869" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6" data-id="element-1870">
          <div className="text-center" data-id="element-1871">
            <p className="text-xl text-[#F5F5F5]/70" data-id="element-1872">Analyzing patterns...</p>
          </div>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-1873">
        <title data-id="element-1874">Kids Decode — Input Collection | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-1875" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-1876">
        {/* Navigation */}
        <header data-id="element-1877">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-1878">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-1879">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-1880">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-1881" />
                <span className="text-lg font-medium tracking-tight" data-id="element-1882">
                  Lazy Monk Labs
                </span>
              </Link>
            </div>
          </nav>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-16" data-id="element-1883">
          <div className="max-w-lg mx-auto w-full" data-id="element-1884">
            {/* Section 1: Child Baseline Setup */}
            {currentSection === 'baseline' && <div data-id="element-1885">
                <div className="text-center mb-12" data-id="element-1886">
                  <h1 className="text-3xl md:text-4xl font-medium mb-4 leading-tight tracking-tight" data-id="element-1887">
                    Child baseline
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-1888">
                    This establishes the child's baseline processing and
                    temperament.
                    <br data-id="element-1889" />
                    It is not an evaluation.
                  </p>
                </div>

                <form onSubmit={handleBaselineSubmit} className="space-y-6" data-id="element-1890">
                  {/* Child's Full Name */}
                  <div data-id="element-1891">
                    <label htmlFor="childName" className="block text-sm font-medium mb-2" data-id="element-1892">
                      Child's Full Name
                    </label>
                    <input type="text" id="childName" value={formData.childName} onChange={e => handleInputChange('childName', e.target.value)} placeholder="Child's full name" required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-1893" />
                  </div>

                  {/* Date of Birth */}
                  <div data-id="element-1894">
                    <label htmlFor="dateOfBirth" className="block text-sm font-medium mb-2" data-id="element-1895">
                      Date of Birth
                    </label>
                    <input type="date" id="dateOfBirth" value={formData.dateOfBirth} onChange={e => handleInputChange('dateOfBirth', e.target.value)} required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-1896" />
                  </div>

                  {/* Time of Birth Option */}
                  <div data-id="element-1897">
                    <label htmlFor="timeOfBirthOption" className="block text-sm font-medium mb-2" data-id="element-1898">
                      Time of Birth
                    </label>
                    <select id="timeOfBirthOption" value={formData.timeOfBirthOption} onChange={e => handleInputChange('timeOfBirthOption', e.target.value)} required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-1899">
                      <option value="" data-id="element-1900">Select option</option>
                      <option value="exact" data-id="element-1901">Exact</option>
                      <option value="approximate" data-id="element-1902">Approximate</option>
                      <option value="unknown" data-id="element-1903">Unknown</option>
                    </select>
                  </div>

                  {/* Time Picker (Conditional) */}
                  {(formData.timeOfBirthOption === 'exact' || formData.timeOfBirthOption === 'approximate') && <div data-id="element-1904">
                      <label htmlFor="timeOfBirth" className="block text-sm font-medium mb-2" data-id="element-1905">
                        Time
                      </label>
                      <input type="time" id="timeOfBirth" value={formData.timeOfBirth} onChange={e => handleInputChange('timeOfBirth', e.target.value)} required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-1906" />
                    </div>}

                  {/* Place of Birth */}
                  <div className="space-y-4" data-id="element-1907">
                    <label className="block text-sm font-medium" data-id="element-1908">
                      Place of Birth
                    </label>
                    <input type="text" id="birthCity" value={formData.birthCity} onChange={e => handleInputChange('birthCity', e.target.value)} placeholder="City" required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-1909" />
                    <input type="text" id="birthCountry" value={formData.birthCountry} onChange={e => handleInputChange('birthCountry', e.target.value)} placeholder="Country" required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-1910" />
                  </div>

                  {/* Submit Button */}
                  <button type="submit" className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg mt-8" data-id="element-1911">
                    Continue
                  </button>
                </form>
              </div>}

            {/* Section 2: Optional Observational Tuning */}
            {currentSection === 'observations' && <div data-id="element-1912">
                <div className="text-center mb-12" data-id="element-1913">
                  <h1 className="text-3xl md:text-4xl font-medium mb-4 leading-tight tracking-tight" data-id="element-1914">
                    Optional observations
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-1915">
                    These help fine-tune how the child's temperament shows up.
                    <br data-id="element-1916" />
                    Skip if unsure.
                  </p>
                </div>

                <div className="space-y-12" data-id="element-1917">
                  {/* Question A: Energy Expression */}
                  <div data-id="element-1918">
                    <h2 className="text-xl font-medium mb-6" data-id="element-1919">
                      How does your child mostly express energy?
                    </h2>
                    <div className="space-y-3" data-id="element-1920">
                      {energyOptions.map((option, index) => <label key={index} className={`block border p-4 cursor-pointer transition-colors ${formData.energyExpression === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1921">
                          <input type="radio" name="energyExpression" value={option} checked={formData.energyExpression === option} onChange={e => handleInputChange('energyExpression', e.target.value)} className="sr-only" data-id="element-1922" />
                          <span className="text-sm" data-id="element-1923">{option}</span>
                        </label>)}
                    </div>
                  </div>

                  {/* Question B: Response to Change */}
                  <div data-id="element-1924">
                    <h2 className="text-xl font-medium mb-6" data-id="element-1925">
                      How does your child usually respond to change?
                    </h2>
                    <div className="space-y-3" data-id="element-1926">
                      {changeResponseOptions.map((option, index) => <label key={index} className={`block border p-4 cursor-pointer transition-colors ${formData.changeResponse === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1927">
                          <input type="radio" name="changeResponse" value={option} checked={formData.changeResponse === option} onChange={e => handleInputChange('changeResponse', e.target.value)} className="sr-only" data-id="element-1928" />
                          <span className="text-sm" data-id="element-1929">{option}</span>
                        </label>)}
                    </div>
                  </div>

                  {/* Question C: Emotional Expression */}
                  <div data-id="element-1930">
                    <h2 className="text-xl font-medium mb-6" data-id="element-1931">
                      When upset, your child tends to:
                    </h2>
                    <div className="space-y-3" data-id="element-1932">
                      {emotionalExpressionOptions.map((option, index) => <label key={index} className={`block border p-4 cursor-pointer transition-colors ${formData.emotionalExpression === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1933">
                          <input type="radio" name="emotionalExpression" value={option} checked={formData.emotionalExpression === option} onChange={e => handleInputChange('emotionalExpression', e.target.value)} className="sr-only" data-id="element-1934" />
                          <span className="text-sm" data-id="element-1935">{option}</span>
                        </label>)}
                    </div>
                  </div>
                </div>

                {/* Generate Button */}
                <button onClick={handleGenerateReport} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg mt-12" data-id="element-1936">
                  Generate Kids Decode
                </button>
              </div>}
          </div>
        </main>
      </div>
    </>;
}